/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentRoutes from 'ContentUtils/Routes';
import Categories from 'ContentUtils/constants/Categories';
import { cloneContent as cloneContentApi, createBlogPost, deleteContent as deleteContentApi, createTranslationVariation as createTranslationVariationApi } from 'ContentEditorUI/api/ContentActionsApi';
// @ts-ignore Untyped module
import BlogsApi from 'ContentEditorUI/api/BlogsApi';
import { CONTENT_MANAGEMENT_ACTION } from 'ContentEditorUI/constants/contentManagement';
// @ts-ignore Untyped module
import { showErrorWithHTML } from 'ContentEditorUI/utils/errorUtils';
import { getContentTypeStringFromCategoryId
// @ts-ignore Untyped module
} from 'ContentEditorUI/utils/publishUtils';
import redirectToNewUrl from 'ContentEditorUI/utils/contentManagementPanel/redirectToNewUrl';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getContentTypeForUsageTracker } from '../utils/contentManagementPanel/contentTableUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};
const getContentTypeTranslationOptions = (categoryId, subcategory) => ({
  translatedContentType: getContentTypeStringFromCategoryId(categoryId, subcategory)
});
const cloneContent = (content, name) => {
  const contentTypeLangKey = content.categoryId === Categories.blog_post.id ? 'blogPost' : 'page';
  const usageTrackingKey = `clone-${getContentTypeForUsageTracker(content.categoryId)}`;
  return cloneContentApi(content, name).then(response => {
    redirectToNewUrl(ContentRoutes.edit(response.id, response.categoryId));
    UsageTracker.trackAsBeacon('editorInteraction', {
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-success`
    });
  }).catch(error => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-failed`
    });
    showErrorWithHTML({
      response: error,
      action: `clone-${contentTypeLangKey}`,
      additionalTranslationOptions: getContentTypeTranslationOptions(content.categoryId, content.subcategory)
    });
  });
};
const deleteContent = (content, onDeleteContent, updateExcludedContentIdsFilters) => {
  const contentTypeLangKey = content.categoryId === Categories.blog_post.id ? 'blogPost' : 'page';
  const usageTrackingKey = `delete-${getContentTypeForUsageTracker(content.categoryId)}`;
  return deleteContentApi(content).then(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-success`
    });
    FloatingAlertStore.addAlert({
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.actions.successes.title"
      }),
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `contentManagementPanel.actions.successes.message.${contentTypeLangKey}.delete`
      }),
      type: 'success'
    });
    onDeleteContent(content);
    updateExcludedContentIdsFilters([content.id]);
  }).catch(error => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${contentTypeLangKey}-failed`
    });
    showErrorWithHTML({
      response: error,
      action: `delete-${contentTypeLangKey}`,
      additionalTranslationOptions: getContentTypeTranslationOptions(content.categoryId, content.subcategory)
    });
  });
};

// If we are creating a translation from a variant, we need to grab some data from the original page
const getMasterLangContentToTranslate = content => {
  const translations = Object.values(content.translatedContent);
  const masterLangContent = translations.filter(translation => !translation.masterId)[0];
  const parentBlog = content.parentBlog;
  const parentBlogTranslatedFromId = parentBlog && parentBlog.translatedFromId;
  masterLangContent.categoryId = masterLangContent.categoryId || content.categoryId;
  masterLangContent.subcategory = masterLangContent.subcategory || content.subcategory;
  if (!masterLangContent.contentGroupId) {
    masterLangContent.contentGroupId = parentBlogTranslatedFromId || content.contentGroupId;
  }
  return masterLangContent;
};
const createContentTranslationVariation = (content, {
  language,
  // language of the variant to be created
  masterLanguage // language of the current page
}, onSuccess, onError) => {
  const contentTypeLangKey = content.categoryId === Categories.blog_post.id ? 'blogPost' : 'page';
  const usageTrackingKey = `translate-${getContentTypeForUsageTracker(content.categoryId)}`;
  const isTranslationVariant = Boolean(content.translatedFromId && content.translatedContent);
  const contentToTranslate = isTranslationVariant ? getMasterLangContentToTranslate(content) : content;

  // Similar logic to what's being done in createBlogLanguageVariantAndTranslateContent above
  // When translating from a variant, we need to grab the language from the original content to set as the master language
  const contentTranslations = Object.values(content.translations);
  const correctedMasterLanguage = isTranslationVariant ? contentTranslations.filter(translation => !translation.masterId)[0].language : masterLanguage;
  return createTranslationVariationApi(contentToTranslate, language, correctedMasterLanguage).then(response => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-success`
    });
    onSuccess(response, {
      newMlangVariantFromMasterLang: isTranslationVariant
    });
  }).catch(error => {
    onError();
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-failed`
    });
    showErrorWithHTML({
      response: error,
      action: `translate-${contentTypeLangKey}`,
      additionalTranslationOptions: getContentTypeTranslationOptions(content.categoryId, content.subcategory)
    });
  });
};
const createBlogLanguageVariantAndTranslateContent = (content, {
  masterLanguage,
  language
}, onSuccess, onError) => {
  const parentBlog = content.parentBlog;
  const parentBlogIsTranslatedVariant = Boolean(parentBlog && parentBlog.translatedFromId);
  const parentBlogTranslations = Object.values(parentBlog.translations);
  const correctedMasterLanguage = parentBlogIsTranslatedVariant ? parentBlogTranslations.filter(translation => !translation.masterId)[0].language : masterLanguage;
  const primaryBlogIdOfContentToUpdate = parentBlogIsTranslatedVariant ? parentBlog.translatedFromId : content.contentGroupId;
  const contentTypeLangKey = content.categoryId === Categories.blog_post.id ? 'blogPost' : 'page';
  const usageTrackingKey = 'create-blog-mlang-variant';
  return BlogsApi.createLanguageVariation({
    contentGroupId: primaryBlogIdOfContentToUpdate,
    masterLanguage: correctedMasterLanguage,
    language
  }).then(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-success`
    });
    createContentTranslationVariation(content, {
      masterLanguage,
      language
    }, onSuccess, onError);
  }).catch(error => {
    onError();
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `${usageTrackingKey}-failed`
    });
    showErrorWithHTML({
      response: error,
      action: `translate-${contentTypeLangKey}`,
      additionalTranslationOptions: getContentTypeTranslationOptions(content.categoryId, content.subcategory)
    });
  });
};
const createTranslationVariation = (content, {
  shouldCreateNewBlogVariation = false,
  masterLanguage,
  // language of the current page
  language // language of the variant to be created
}, onSuccess, onError) => {
  const contentType = content.contentTypeCategoryId;
  if (contentType === Categories.blog_post.id && shouldCreateNewBlogVariation) {
    createBlogLanguageVariantAndTranslateContent(content, {
      masterLanguage,
      language
    }, onSuccess, onError);
  } else {
    createContentTranslationVariation(content, {
      masterLanguage,
      language
    }, onSuccess, onError);
  }
};
const createContent = blogId => {
  return createBlogPost(blogId).then(response => {
    redirectToNewUrl(ContentRoutes.edit(response.id, response.categoryId));
  }).catch(error => {
    showErrorWithHTML({
      response: error,
      action: `create-blogPost`,
      additionalTranslationOptions: getContentTypeTranslationOptions(Categories.blog_post.id)
    });
  });
};
const useContentModalActions = (onDeleteContent, updateExcludedContentIdsFilters, onCreateTranslationVariation = noop, onCreateTranslationVariationError = noop) => {
  return {
    cloneContent,
    deleteContent: content => deleteContent(content, onDeleteContent, updateExcludedContentIdsFilters),
    createContent,
    createTranslationVariation: (content, opts) => createTranslationVariation(content, opts, onCreateTranslationVariation, onCreateTranslationVariationError)
  };
};
export default useContentModalActions;